import { PreloadJob } from '../../../UnicronApp/preloader/PreloadJob';
import { NonEmptyArray } from '../../util/NonEmptyArray';
import { safeFromEntries } from '../../util/flatMapObject';
import { TimeoutError, withTimeout } from '../../util/withTimeout';
import { applyTargetCookies } from '../server/applyTargetCookies';
import { resolveServerExperiments } from '../server/resolveServerExperiments.server-only';
import { Experiment, ExperimentCache } from '../types';
import { ExperimentSlot } from './ExperimentSlot';

const EXECUTION_TIMEOUT_MS = 210;

/** Fetches experiment values from Adobe Target without "executing" them, meaning no impression
 * gets recorded. If the preloaded experiment is used on the client side (with useExperiment),
 * the "execution" and the recording of an impression will happen upon first use.
 */
export const preloadExperiments = async (
  job: PreloadJob,
  experiments: NonEmptyArray<Experiment<unknown> | null>,
): Promise<ExperimentCache | undefined> => {
  const { server, services } = job;
  const { cache, logger } = services;

  if (server && experiments.length > 0) {
    return await logger.profile('Preloading experiments', async addLogContext => {
      /** On the client, this will be undefined and we must bail out. */
      if (!resolveServerExperiments) return;

      try {
        const resolved = await withTimeout(
          EXECUTION_TIMEOUT_MS,
          resolveServerExperiments(server.req, experiments, logger),
        );

        const { responseCookies, experimentValues, responseContext } = resolved;

        Object.entries(responseContext ?? {}).forEach(([key, value]) => addLogContext(key, value));

        const filteredExperiments = experiments.filter((a): a is Experiment<unknown> => a !== null);

        const experimentCache = {
          ...safeFromEntries<ExperimentCache>(
            filteredExperiments.map(it => ({ key: it.mbox, value: { value: undefined } })),
          ),
          ...(cache.get(ExperimentSlot) ?? {}),
          ...experimentValues,
        };

        cache.set(ExperimentSlot, experimentCache);

        if (responseCookies) applyTargetCookies(server.res, responseCookies);
        return experimentCache;
      } catch (error) {
        if (error instanceof TimeoutError) {
          logger.error(`Timed out (${EXECUTION_TIMEOUT_MS}ms) while prefetching experiments`);
        } else {
          logger.error('Failed to preload experiments', error);
        }

        return undefined;
      }
    });
  } else {
    return cache.get(ExperimentSlot);
  }
};
